import { Offer, RollingOfferProductSequence } from 'common/contracts';
import { MIN_USD_PRICE } from 'constants/constants';
import { OfferType } from 'constants/enums';
import Decimal from 'decimal.js';

import {
  buildBaseInitialValues,
  buildOfferUiIdInitialValue,
  buildScheduleInitialValue
} from 'views/Offers/Offer.utils';

export const DEFAULT_FREE_TILE_COUNT = 2;
export const MIN_FREE_TILE_COUNT = 1;
export const MAX_FREE_TILE_COUNT = 5;

export const resolveDesignFreeTileCount = (
  freeTilesLayout: number | undefined
): number => {
  if (freeTilesLayout === undefined) return DEFAULT_FREE_TILE_COUNT;
  if (freeTilesLayout < MIN_FREE_TILE_COUNT) return MIN_FREE_TILE_COUNT;
  if (freeTilesLayout > MAX_FREE_TILE_COUNT) return MAX_FREE_TILE_COUNT;
  return freeTilesLayout;
};

export const buildInitialProductsSequence = (
  displayName: string,
  freeTileCount: number
): RollingOfferProductSequence[] =>
  Array.from({ length: 1 + freeTileCount }, (_, position) => ({
    index: position + 1,
    priceInUsdCents: 0,
    displayName,
    setAsFree: position !== 0,
    products: [{ product: { images: [] } }]
  }));

export const convertPriceToCents = (price: number): number => {
  if (price === 0) return 0;
  return new Decimal(price).mul(100).toNumber();
};

export const convertPriceFromCents = (priceInCents: number): number => {
  if (priceInCents === 0) return 0;
  return new Decimal(priceInCents).div(100).toNumber();
};

export const validateNumericField = (
  value: number | undefined | null,
  { min, max, fieldName }: { min?: number; max?: number; fieldName: string }
): string | null => {
  if (value === undefined || value === null) return null;
  if (min !== undefined && value < min)
    return `${fieldName} must be at least ${min}`;
  if (max !== undefined && value > max)
    return `${fieldName} must be at most ${max}`;
  return null;
};

interface IBuildBuyOneGetManyFormInitialValuesParams {
  data: Partial<Offer>;
  edit: boolean;
  dup: boolean;
  defaultOfferDesignId: string;
  getOfferResult?: Partial<Offer>;
}

export const buildBuyOneGetManyFormInitialValues = ({
  data,
  edit,
  dup,
  defaultOfferDesignId,
  getOfferResult
}: IBuildBuyOneGetManyFormInitialValuesParams) => ({
  ...buildBaseInitialValues(data, edit, dup),
  type: OfferType.BUY_ONE_GET_MANY,
  priority: data.priority || 1,
  offerUiId: buildOfferUiIdInitialValue(
    data,
    edit,
    dup,
    defaultOfferDesignId,
    getOfferResult
  ),
  schedule: buildScheduleInitialValue(data),
  price: MIN_USD_PRICE,
  productsSequence: data.productsSequence
    ? (data.productsSequence as RollingOfferProductSequence[]).map(
        (product: RollingOfferProductSequence) => ({
          ...product,
          progressBarPoints: product.progressBarPoints || [
            { points: 0, barId: '' }
          ],
          priceInUsdCents: convertPriceFromCents(product?.priceInUsdCents || 0)
        })
      )
    : []
});
